import { type FC, type PropsWithChildren, useEffect, useMemo, useRef } from 'react'
import type { VBarProps } from '../../vbar/VBar'
import { createVBarStackAPI } from '../core'
import type { VBarStackProps as BaseVBarStackProps, VBarStackClusterProps } from '../types'
import { VBarCluster } from './VBarCluster'
import { PointerPositionContext } from './PointerPositionContext'
import { defaultVBarFontMetrics, VBarFontMetricsContext } from '../../vbar/VBarFontMetricsContext'

/**
 * Metadata used by the cluster item to render a placeholder for the cluster.
 * This data is provided by each VBar item that is part of the cluster.
 */
export type ClusterItemMetadata = Pick<
  VBarProps,
  | 'color'
  | 'strokeColor'
  | 'fillColor'
  | 'primaryTextColor'
  | 'secondaryTextColor'
  | 'maxTextWidth'
  | 'lineHeight'
> & {
  readonly left?: number
  readonly width?: number
  readonly zIndex?: number
}

const { VBarStack: InternalVBarStack, useVBarStackItem } = createVBarStackAPI<ClusterItemMetadata>()

export interface VBarStackProps
  extends Omit<BaseVBarStackProps<ClusterItemMetadata>, 'VBarCluster'> {
  /**
   * When defined, this line-height will be used by child vbar components,
   * and it will be used for all line-height based calculations.
   * When not provided, it defaults to `DefaultLineHeight`, which is the line-height of small text in GDS (18.052px).
   */
  readonly lineHeight?: number
  /**
   * Minimum content height of a collapsed cluster in `lineHeight` units.
   * If not provided, the minimum content height will be calculated based on the items in the cluster.
   * Defaults to 2.
   */
  readonly collapsedClusterMinContentHeightLines?: number
  /**
   * Gap between expanded clusters in `lineHeight` units.
   * This can be used to add spacing between clusters (individual VBars are considered singleton clusters).
   * Defaults to 1.
   */
  readonly interClusterGapLines?: number
  /**
   * Margin at the bottom of an expanded cluster in `lineHeight` units.
   * This can be used to add spacing after the items of an expanded cluster.
   * If `interClusterGap` is provided, the maximum value of either will be used as the bottom margin below an expanded cluster.
   * Defaults to 1.
   */
  readonly expandedClusterBottomMarginLines?: number
  /**
   * Delay in milliseconds before a cluster is collapsed when all items are marked inactive.
   * Defaults to 500.
   */
  readonly clusterCollapseDelayMs?: number
  /**
   * The cluster component to use. Defaults to `DefaultVBarCluster`
   */
  readonly VBarCluster?: FC<VBarStackClusterProps<ClusterItemMetadata>>
}

const VBarStack: FC<PropsWithChildren<VBarStackProps>> = ({
  children,
  interClusterGap,
  interClusterGapLines = 1,
  expandedClusterBottomMargin,
  expandedClusterBottomMarginLines = 1,
  collapsedClusterMinContentHeight,
  collapsedClusterMinContentHeightLines = 2,
  clusterCollapseDelayMs = 500,
  containerScrollHeight = Number.MAX_SAFE_INTEGER,
  ...props
}) => {
  const fontMetrics = useMemo(
    () => ({
      ...defaultVBarFontMetrics,
      lineHeight: props.lineHeight ?? defaultVBarFontMetrics.lineHeight,
    }),
    [props.lineHeight],
  )

  const lastPointerPosition =
    useRef<ReturnType<PointerPositionContext['getLastPointerPosition']>>(null)

  const pointerPositionContextValue = useMemo<PointerPositionContext>(
    () => ({
      getLastPointerPosition: () => lastPointerPosition.current,
    }),
    [],
  )

  useEffect(() => {
    const handlePointerEvent = (event: PointerEvent | MouseEvent) => {
      lastPointerPosition.current = {
        clientX: event.clientX,
        clientY: event.clientY,
      }
    }
    document.addEventListener('pointermove', handlePointerEvent)
    document.addEventListener('click', handlePointerEvent)
    return () => {
      document.removeEventListener('pointermove', handlePointerEvent)
      document.removeEventListener('click', handlePointerEvent)
    }
  }, [])

  return (
    <VBarFontMetricsContext.Provider value={fontMetrics}>
      <PointerPositionContext.Provider value={pointerPositionContextValue}>
        <InternalVBarStack
          {...props}
          containerScrollHeight={containerScrollHeight}
          VBarCluster={props.VBarCluster ?? VBarCluster}
          clusterCollapseDelayMs={clusterCollapseDelayMs}
          interClusterGap={
            interClusterGap ??
            (interClusterGapLines ? interClusterGapLines * fontMetrics.lineHeight : undefined)
          }
          expandedClusterBottomMargin={
            expandedClusterBottomMargin ??
            (expandedClusterBottomMarginLines
              ? expandedClusterBottomMarginLines * fontMetrics.lineHeight
              : undefined)
          }
          collapsedClusterMinContentHeight={
            collapsedClusterMinContentHeight ??
            (collapsedClusterMinContentHeightLines
              ? collapsedClusterMinContentHeightLines * fontMetrics.lineHeight
              : undefined)
          }
        >
          {children}
        </InternalVBarStack>
      </PointerPositionContext.Provider>
    </VBarFontMetricsContext.Provider>
  )
}

export { VBarStack, useVBarStackItem }
